/**
 * Navigation helpers for Admin Dashboard → Listings return context.
 *
 * When an admin opens a listing from the Listings tab the View URL includes
 * ?returnTo=adminListings so the editor/viewer can show "Back to Admin Listings"
 * and return to /admin?tab=listings.
 */

export const ADMIN_LISTINGS_RETURN_KEY = 'adminListings';

/** URL the back button navigates to when returning from admin listings context. */
export const ADMIN_LISTINGS_RETURN_URL = '/admin?tab=listings';

/**
 * Editor URL for a listing opened from Admin → Listings.
 * Appends ?returnTo=adminListings so ListingEditor knows where to return.
 */
export function buildAdminListingEditorUrl(listingId) {
  return `/owner/listings/${listingId}/edit?returnTo=${ADMIN_LISTINGS_RETURN_KEY}`;
}

/**
 * Public listing URL for a published listing opened from Admin → Listings.
 * Appends ?returnTo=adminListings so PropertyDetailPage knows where to return.
 */
export function buildAdminListingPublicUrl(listingId) {
  return `/listings/${listingId}?returnTo=${ADMIN_LISTINGS_RETURN_KEY}`;
}

/**
 * Returns true when the page was opened from Admin → Listings.
 * Reads ?returnTo from the given URLSearchParams instance.
 */
export function isFromAdminListings(searchParams) {
  return searchParams.get('returnTo') === ADMIN_LISTINGS_RETURN_KEY;
}

/**
 * Reads ?tab= from searchParams and validates it against allowedTabs.
 * Returns the tab string if valid, null otherwise.
 * Call this in AdminPage to activate the correct tab when navigating back.
 */
export function parseAdminTabFromSearchParams(searchParams, allowedTabs) {
  const tab = searchParams.get('tab');
  if (!tab) return null;
  return allowedTabs.includes(tab) ? tab : null;
}

/**
 * Returns true when the owner-facing "Submit for Review" CTA should be shown.
 * Hidden in admin review mode to prevent circular re-submission.
 */
export function shouldShowSubmitForReview(fromAdminListings) {
  return !fromAdminListings;
}

/**
 * Returns true when the admin return CTA should appear in place of the
 * "Submit for Review" button — only on the final step (5) in admin review mode.
 */
export function shouldShowAdminReturnCta(fromAdminListings, currentStep) {
  return !!fromAdminListings && currentStep === 5;
}

/** @deprecated Use shouldShowAdminReturnCta */
export function shouldShowAdminCtaHelperText(fromAdminListings, currentStep) {
  return shouldShowAdminReturnCta(fromAdminListings, currentStep);
}

/**
 * Open a listing from Admin → Listings in the current tab (never window.open).
 * Published listings go to public detail; others go to the owner editor.
 */
export function navigateToAdminListingView(navigate, listingId, { isPublished = false } = {}) {
  if (typeof navigate !== 'function' || !listingId) {
    return;
  }
  const url = isPublished
    ? buildAdminListingPublicUrl(listingId)
    : buildAdminListingEditorUrl(listingId);
  navigate(url);
}

/**
 * Step 5 action layout for listing editor (admin vs owner).
 * Admin review mode exposes exactly one Return button (footer only).
 */
export function getAdminEditorStepFiveActions(fromAdminListings) {
  const adminReturn = shouldShowAdminReturnCta(fromAdminListings, 5);
  return {
    showAdminPhotosHint: adminReturn,
    showAdminReturnFooter: adminReturn,
    showSubmitForReview: shouldShowSubmitForReview(fromAdminListings),
    adminReturnButtonCount: adminReturn ? 1 : 0,
  };
}

/**
 * Navigate back to Admin → Listings in the current tab (never window.open).
 */
export function navigateToAdminListings(navigate, { replace = false } = {}) {
  if (typeof navigate !== 'function') {
    return;
  }
  navigate(ADMIN_LISTINGS_RETURN_URL, { replace });
}

/**
 * Documents same-tab return contract for tests (must not use window.open).
 */
export function getAdminListingsReturnNavigation() {
  return {
    url: ADMIN_LISTINGS_RETURN_URL,
    sameTab: true,
    method: 'navigate',
  };
}
